import { isLeadnamicsDomain } from 'hooks/useBrand';

export const COOKIEFIRST_CONSENT_EVENTS = ['cf_consent_loaded', 'cf_consent', 'cf_consent_done'] as const;

export type CookieFirstCategory = 'necessary' | 'functional' | 'performance' | 'advertising';

export interface CookieFirstConsent {
  necessary: boolean;
  functional: boolean;
  performance: boolean;
  advertising: boolean;
}

interface CookieFirstApi {
  consent?: Partial<Record<CookieFirstCategory, boolean>> | null;
  openPanel?: () => void;
}

declare global {
  interface Window {
    CookieFirst?: CookieFirstApi;
  }
}

const COOKIEFIRST_CATEGORIES: CookieFirstCategory[] = ['necessary', 'functional', 'performance', 'advertising'];

const hasKnownConsentValue = (consent: Partial<Record<CookieFirstCategory, boolean>>): boolean => {
  return COOKIEFIRST_CATEGORIES.some((category) => typeof consent[category] === 'boolean');
};

const STAGING_HOSTNAMES = new Set(['app-staging.lidz.ai']);
const STAGING_HOSTNAME_FRAGMENTS = ['235999303981', 'lidz-front-ephemeral-'];

const isStagingDomain = (): boolean =>
  typeof window !== 'undefined' &&
  (STAGING_HOSTNAMES.has(window.location.hostname) ||
    STAGING_HOSTNAME_FRAGMENTS.some((fragment) => window.location.hostname.includes(fragment)));

export const hasCookieFirstScriptConfigured = (): boolean =>
  !isLeadnamicsDomain() && !isStagingDomain() && Boolean(import.meta.env.VITE_COOKIEFIRST_SCRIPT_SRC);

const canBypassCookieFirstConsent = (): boolean => !import.meta.env.PROD;

export const getCookieFirstConsent = (): CookieFirstConsent | null => {
  if (typeof window === 'undefined') return null;

  const consent = window.CookieFirst?.consent;
  if (!consent || !hasKnownConsentValue(consent)) {
    return null;
  }

  return {
    necessary: consent.necessary !== false,
    functional: consent.functional === true,
    performance: consent.performance === true,
    advertising: consent.advertising === true,
  };
};

export const getCookieFirstConsentFromEvent = (event: Event): CookieFirstConsent | null => {
  if (!(event instanceof CustomEvent)) {
    return null;
  }

  const consent = event.detail as Partial<Record<CookieFirstCategory, boolean>> | null | undefined;
  if (!consent || !hasKnownConsentValue(consent)) {
    return null;
  }

  return {
    necessary: consent.necessary !== false,
    functional: consent.functional === true,
    performance: consent.performance === true,
    advertising: consent.advertising === true,
  };
};

export const isCookieFirstCategoryAccepted = (
  category: CookieFirstCategory,
  consent: CookieFirstConsent | null = getCookieFirstConsent()
): boolean => {
  if (!consent) {
    return false;
  }

  return consent[category] === true;
};

export const canUseCookieFirstCategory = (
  category: CookieFirstCategory,
  consent: CookieFirstConsent | null = getCookieFirstConsent()
): boolean => {
  if (isLeadnamicsDomain()) return true;
  if (isStagingDomain()) return true;
  return (
    (!hasCookieFirstScriptConfigured() && canBypassCookieFirstConsent()) ||
    isCookieFirstCategoryAccepted(category, consent)
  );
};

export const openCookiePreferencesPanel = (): void => {
  if (typeof window === 'undefined') return;
  window.CookieFirst?.openPanel?.();
};

export const addCookieFirstConsentListener = (listener: EventListener): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  COOKIEFIRST_CONSENT_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, listener);
  });

  return () => {
    COOKIEFIRST_CONSENT_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, listener);
    });
  };
};

export const subscribeToConsent = (onConsentChange: (consent: CookieFirstConsent | null) => void): (() => void) => {
  return addCookieFirstConsentListener((event) => {
    onConsentChange(getCookieFirstConsentFromEvent(event) ?? getCookieFirstConsent());
  });
};

let cachedConsent: CookieFirstConsent | null = null;
const consentListeners = new Set<(consent: CookieFirstConsent | null) => void>();

let consentCacheUnsubscribe: (() => void) | null = null;

export const initConsentCache = (): void => {
  consentCacheUnsubscribe?.();
  cachedConsent = getCookieFirstConsent();
  consentCacheUnsubscribe = addCookieFirstConsentListener((event) => {
    cachedConsent = getCookieFirstConsentFromEvent(event) ?? getCookieFirstConsent();
    consentListeners.forEach((fn) => fn(cachedConsent));
  });
};

export const getCachedConsent = (): CookieFirstConsent | null => cachedConsent;

export const subscribeToConsentCache = (fn: (consent: CookieFirstConsent | null) => void): (() => void) => {
  consentListeners.add(fn);
  return () => consentListeners.delete(fn);
};
